import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { toast } from 'react-toastify'

import { setSelectedHub } from 'slices/auth/reducer'
import { storePAN, validatePAN } from 'services/api/wallet'
import { AppDispatch, RootState } from 'store'
import { PANDetails } from 'types/wallet'
import { getErrorMessage } from 'utils/helpers'
import inModal from 'utils/hoc/inModal'

const PAN_REGEX = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/

const formatPAN = (value: string) =>
    value.replace(/[^A-Za-z0-9]/g, '').toUpperCase().slice(0, 10)

const getPANValidationError = (value: string): string | undefined => {
    const trimmed = value.trim()
    if (!trimmed) return 'PAN is required.'
    if (trimmed.length !== 10) return 'PAN must be 10 characters.'
    if (!PAN_REGEX.test(trimmed)) {
        return 'Please enter a valid PAN (e.g. ABCDE1234F).'
    }
    return undefined
}

interface AddPANModalProps {
    isActive: boolean
    onClose: () => void
}

const AddPANModal: React.FC<AddPANModalProps> = ({ isActive, onClose }) => {
    const dispatch = useDispatch<AppDispatch>()
    const currentPan = useSelector((state: RootState) => state.auth.selectedHub?.pan)
    const selectedHub = useSelector((state: RootState) => state.auth.selectedHub)
    const selectedHubId = useSelector((state: RootState) => state.auth.selectedHubId)
    const [pan, setPan] = useState('')
    const [errors, setErrors] = useState<Record<string, string>>({})
    const [validatedPan, setValidatedPan] = useState<PANDetails | null>(null)
    const [processing, setProcessing] = useState(false)

    const isValidated = validatedPan !== null

    useEffect(() => {
        if (!isActive) return
        setPan(currentPan ?? '')
        setErrors({})
        setValidatedPan(null)
    }, [isActive, currentPan])

    const validatePANInput = () => {
        const panError = getPANValidationError(pan)
        if (panError) {
            setErrors({ pan: panError })
            return false
        }
        setErrors({})
        return true
    }

    const handleValidate = async () => {
        if (!validatePANInput() || !selectedHubId) return
        try {
            setProcessing(true)
            const response = await validatePAN(selectedHubId, pan.trim())
            if (response.status && response.pan) {
                setValidatedPan(response.pan)
                toast.success('PAN validated successfully.')
            } else {
                toast.error('PAN validation failed. Please check the PAN and try again.')
            }
        } catch (error) {
            setErrors({ pan: 'Invalid PAN. Please check the PAN and try again.' })
        } finally {
            setProcessing(false)
        }
    }

    const handleSave = async () => {
        if (!validatedPan || !selectedHubId) return
        try {
            setProcessing(true)
            await storePAN(selectedHubId, validatedPan.id)
            if (selectedHub) {
                dispatch(setSelectedHub({ ...selectedHub, pan: validatedPan.pan }))
            }
            toast.success('PAN saved successfully.')
            onClose()
        } catch (error) {
            toast.error(getErrorMessage(error, 'Failed to save PAN. Please try again.'))
        } finally {
            setProcessing(false)
        }
    }

    const handlePrimaryClick = () => {
        if (isValidated) {
            handleSave()
        } else {
            handleValidate()
        }
    }

    if (!isActive) return null

    return (
        <div className="add-pan-modal">
            <div className="mb-3">
                <label htmlFor="pan-number" className="form-label">
                    PAN <span className="text-danger">*</span>
                </label>
                <input
                    id="pan-number"
                    type="text"
                    className={`form-control text-uppercase ${errors.pan ? 'is-invalid' : ''}`}
                    name="pan"
                    value={pan}
                    disabled={isValidated || processing}
                    placeholder="ABCDE1234F"
                    maxLength={10}
                    autoComplete="off"
                    onChange={(e) => {
                        setPan(formatPAN(e.target.value))
                        setErrors((prev) => ({ ...prev, pan: '' }))
                        setValidatedPan(null)
                    }}
                    onBlur={validatePANInput}
                />
                {errors.pan && <div className="invalid-feedback d-block">{errors.pan}</div>}
            </div>

            {validatedPan && (
                <div className="add-pan-modal__details border rounded p-3 mb-3 bg-light">
                    <h6 className="mb-3">PAN details</h6>
                    <dl className="row mb-0 small">
                        <dt className="col-sm-4 text-muted">PAN</dt>
                        <dd className="col-sm-8 text-uppercase">{validatedPan.pan}</dd>
                        <dt className="col-sm-4 text-muted">Registered name</dt>
                        <dd className="col-sm-8">{validatedPan.registered_name}</dd>
                    </dl>
                </div>
            )}

            <button
                type="button"
                className="theme-btn btn-primary w-100"
                disabled={processing || (!isValidated && !pan.trim())}
                onClick={handlePrimaryClick}
            >
                {processing
                    ? isValidated
                        ? 'Saving...'
                        : 'Validating...'
                    : isValidated
                      ? 'Save'
                      : 'Validate'}
            </button>
        </div>
    )
}

export default inModal(AddPANModal, 'Add PAN', {
    id: 'add-pan-modal',
    modalHeaderClassName: 'border-bottom pb-3',
})
